// The SOP as a document, written from the map: every step's instruction once, in flow order, then each scenario as
// its own work instruction assembled from the same steps. Agent lanes say so: people and agents follow the same
// procedure. Print it, or save it as a PDF from the print dialog.
import { byId, doneBy, outs, pathOf, wiNumber, type MapBody, type Released } from "@couragemfg/api/processes-model";
import { layout } from "./layout";
import { sysText } from "./MapCanvas";
import { ScreenText } from "./OpenScreen";
import { fmtDate, type ProcessDetail } from "./processApi";

export function SopView({ d, which, onWhich, draftRev, hasDraft }: { d: ProcessDetail; which: "draft" | "released"; onWhich: (w: "draft" | "released") => void; draftRev: string; hasDraft: boolean; }) {
  const m: MapBody | Released | null = which === "released" ? d.released : d.draft;
  if (!m) return <div className="pmap-doc-wrap"><p className="pmap-note">Nothing released yet.</p></div>;
  const L = layout(m), B = byId(m), scen = "scenarios" in m ? m.scenarios : [];
  const lane = (id: string) => m.lanes.find((l) => l.id === id)?.name ?? "";
  const n = (id: string) => L.ordered.indexOf(id) + 1;
  const rev = which === "released" ? `Rev ${d.rev}` : `Rev ${draftRev} draft`;
  const base = d.docNo.replace(/^SOP-/i, "");
  return (
    <div className="pmap-doc-wrap">
      <div className="pmap-doc-bar no-print">
        {hasDraft && d.released && (
          <div className="pmap-seg" role="group" aria-label="Which revision">
            <button aria-pressed={which === "released"} onClick={() => onWhich("released")}>Rev {d.rev} in effect</button>
            <button aria-pressed={which === "draft"} onClick={() => onWhich("draft")}>Rev {draftRev} draft</button>
          </div>
        )}
        <button className="btn" onClick={() => window.print()}>Print or save as PDF</button>
      </div>
      <article className="pmap-doc">
        <header>
          <div className="pmap-doc-no"><span className="mono">{d.docNo}</span><span>{rev}</span>{which === "draft" && <span className="pmap-status warn">Not released</span>}</div>
          <h1>{d.title}</h1>
          <dl className="pmap-kv">
            <dt>Area</dt><dd>{d.areaName || "–"}</dd>
            <dt>Process owner</dt><dd>{d.owner || "–"}</dd>
            {which === "released" && <><dt>Effective</dt><dd>{fmtDate(d.releasedAt)}</dd><dt>Approved by</dt><dd>{d.releasedByName ?? "–"}</dd></>}
            {d.docRecord && <><dt>Document control</dt><dd className="mono">{d.docRecord.number}</dd></>}
          </dl>
        </header>
        {d.summary && <section><h2>Purpose</h2><p>{d.summary}</p></section>}
        <section>
          <h2>Roles</h2>
          <ul className="pmap-doc-roles">{m.lanes.map((l) => <li key={l.id}><b>{l.name}</b>{l.agent ? " (AI agent)" : ""} · {m.steps.filter((s) => s.lane === l.id && s.type !== "start" && s.type !== "end").map((s) => s.name).join(", ") || "no steps"}</li>)}</ul>
          {m.lanes.some((l) => l.agent) && <p className="pmap-note">An AI agent does the steps in an agent lane, through CourageMFG and Business Central, following this revision as written.</p>}
        </section>
        <section>
          <h2>Procedure</h2>
          <ol className="pmap-doc-steps">
            {L.ordered.map((id) => {
              const s = B[id]; if (!s) return null;
              const o = outs(m, id);
              return (
                <li key={id}>
                  <div className="h"><span className="mono">{wiNumber(d.docNo, n(id))}</span><b>{s.name}</b></div>
                  <div className="meta">{lane(s.lane)}{doneBy(m, s) === "agent" && s.type !== "start" && s.type !== "end" ? " · done by an agent" : ""}{s.owner && s.owner !== lane(s.lane) ? ` · ${s.owner}` : ""}{sysText(s) ? <> · <ScreenText s={s} /></> : ""}</div>
                  {s.instruction && <p>{s.instruction}</p>}
                  {s.type === "decision" && o.length > 0 && <ul className="br">{o.map((f) => <li key={f.to}>{f.label || "Then"}: go to {B[f.to]?.name} ({wiNumber(d.docNo, n(f.to))})</li>)}</ul>}
                  {(s.control || s.kpi) && <div className="ck">{s.control && <span><b>Control:</b> {s.control}</span>}{s.kpi && <span><b>KPI:</b> {s.kpi}</span>}</div>}
                </li>
              );
            })}
          </ol>
        </section>
        {scen.length > 0 && (
          <section>
            <h2>Scenario work instructions</h2>
            <p className="pmap-note">Each scenario is one path through the procedure, assembled from the steps above.</p>
            {scen.map((sc, k) => {
              const p = pathOf(m, sc).filter((id) => B[id] && B[id].type !== "end");
              return (
                <div key={sc.id} className="pmap-doc-scen">
                  <h3><span className="mono">WI-{base}-S{k + 1}</span> {sc.name}</h3>
                  {sc.condition && <div className="meta">Starts when: {sc.condition}</div>}
                  <ol>{p.map((id) => <li key={id}><b>{B[id].name}</b> <span className="mono">{wiNumber(d.docNo, n(id))}</span>{B[id].instruction ? <> · {B[id].instruction}</> : null}</li>)}</ol>
                </div>
              );
            })}
          </section>
        )}
        {d.revisions.length > 0 && (
          <section>
            <h2>Revision history</h2>
            <table className="pmap-doc-hist"><thead><tr><th>Rev</th><th>Date</th><th>By</th><th>Change</th></tr></thead>
              <tbody>{d.revisions.map((r) => <tr key={r.rev}><td className="mono">{r.rev}</td><td>{fmtDate(r.releasedAt)}</td><td>{r.releasedByName}</td><td>{r.note}</td></tr>)}</tbody></table>
          </section>
        )}
      </article>
    </div>
  );
}
